"use client"

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";
import { focusVisibleOutline } from "@/lib/focus-styles";
import { useScrollActiveChildIntoView } from "@/hooks/use-scroll-active-child-into-view";
import { useMediaQuery } from "@/hooks/use-media-query";

const LG_UP_MEDIA_QUERY = "(min-width: 1024px)";

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg py-[3px] px-0 text-muted-foreground group-data-[orientation=horizontal]/tabs:h-9 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function TabsList({
  className,
  variant = "default",
  scrollActiveTabOnMobile = false,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants> & {
    scrollActiveTabOnMobile?: boolean;
  }) {
  const listRef = React.useRef<React.ComponentRef<typeof TabsPrimitive.List>>(null);
  const isLgUp = useMediaQuery(LG_UP_MEDIA_QUERY);

  useScrollActiveChildIntoView(listRef, '[data-state="active"]', {
    enabled: scrollActiveTabOnMobile && !isLgUp,
    watchActiveChanges: scrollActiveTabOnMobile,
  });

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  onMouseDown,
  onClick,
  onBlur,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  // The scripted focus() below may match :focus-visible depending on the previous
  // focus state, so the ring is suppressed explicitly while focus comes from the mouse.
  const [pointerFocus, setPointerFocus] = React.useState(false);

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      data-pointer-focus={pointerFocus || undefined}
      // Radix activates the tab on mousedown (WCAG 2.5.2 Pointer Cancellation).
      // Cancel it and activate on click instead: focusing the trigger selects it
      // (automatic activation), so the pointer up event completes the action.
      onMouseDown={(event) => {
        onMouseDown?.(event);
        event.preventDefault();
        setPointerFocus(true);
      }}
      onClick={(event) => {
        onClick?.(event);
        event.currentTarget.focus();
      }}
      onBlur={(event) => {
        onBlur?.(event);
        setPointerFocus(false);
      }}
      className={cn(
        "cursor-pointer h-[32px] relative inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent py-2 px-4 label-m whitespace-nowrap text-foreground transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground not-data-[pointer-focus]:focus-visible:border-ring not-data-[pointer-focus]:focus-visible:ring-[3px] not-data-[pointer-focus]:focus-visible:ring-primary not-data-[pointer-focus]:focus-visible:outline-1 data-[pointer-focus]:outline-none disabled:pointer-events-none disabled:opacity-50 group-data-[variant=default]/tabs-list:data-[state=active]:shadow-sm group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none dark:text-muted-foreground dark:hover:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:border-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent",
        "data-[state=active]:bg-background data-[state=active]:text-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:data-[state=active]:text-foreground",
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        "flex-1",
        // The panel is tabbable (Radix sets tabindex=0): the ring is offset so it keeps a gap from the content.
        focusVisibleOutline,
        className
      )}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
